import { DialogComponent } from '@theme/dialog';
import { CartUpdateEvent } from '@theme/events';
import { buildShareCartUrl, findUnserialisableLines, isThirdPartyCartLine } from '@theme/share-cart-url';

/** Medium error correction keeps long share-cart URLs scannable at modal size. */
const QR_CORRECT_LEVEL_M = 0;
const QR_SIZE = 240;

/**
 * 3rd Party Cart QR checkout.
 *
 * The cart CTA (`[data-third-party-cart-checkout-open]`, rendered by
 * snippets/cart-summary.liquid when the cart's external checkout type is
 * `third_party_cart`) opens this dialog. It builds the Bunnings share-cart URL
 * from the 3rd Party Cart lines and shows it as a QR code. The Shopify cart is
 * cleared only when the customer taps Done (or continues on this device);
 * closing any other way leaves it intact. See
 * docs/business-rules/teso-promotion.md (3rd Party Cart checkout).
 *
 * @typedef {object} ThirdPartyCartCheckoutRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} qrHost
 * @property {HTMLElement} loadingMessage
 * @property {HTMLAnchorElement} deviceLink
 * @property {HTMLButtonElement} doneButton
 * @property {HTMLElement} errorMessage
 *
 * @extends {DialogComponent<ThirdPartyCartCheckoutRefs>}
 */
class ThirdPartyCartCheckoutDialog extends DialogComponent {
  /** @type {string} */
  #handoffUrl = '';

  /** @type {boolean} */
  #busy = false;

  /** @returns {Promise<void>} */
  async open() {
    if (this.#busy) return;
    this.#busy = true;
    this.#handoffUrl = '';
    this.#reset();
    this.showDialog();

    let missingSku = false;

    try {
      const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!cartResponse.ok) throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
      const cart = await cartResponse.json();

      const items = (cart.items || []).filter(isThirdPartyCartLine);
      if (items.length === 0) throw new Error('No 3rd Party Cart lines in cart');

      // buildShareCartUrl skips SKU-less lines, and Done clears the cart, so an
      // incomplete QR would silently lose items.
      const droppedItems = findUnserialisableLines(items);
      if (droppedItems.length > 0) {
        missingSku = true;
        throw new Error(`${droppedItems.length} 3rd Party Cart line(s) have no usable SKU`);
      }

      const url = buildShareCartUrl(
        { items },
        { baseUrl: this.dataset.baseUrl || '', utmSource: this.dataset.utmSource || '' }
      );
      if (!url) throw new Error('Share-cart URL could not be built');

      const { QRCode } = await import('@theme/qr-code-generator');
      const { qrHost, deviceLink, loadingMessage } = this.refs;
      qrHost.replaceChildren();
      new QRCode(qrHost, {
        text: url,
        width: QR_SIZE,
        height: QR_SIZE,
        alt: this.dataset.qrAlt || '',
        correctLevel: QR_CORRECT_LEVEL_M,
      });
      // The library mirrors the encoded URL into `title`; the alt text is enough.
      qrHost.removeAttribute('title');

      deviceLink.href = url;
      deviceLink.hidden = false;
      loadingMessage.hidden = true;
      this.#handoffUrl = url;
    } catch (error) {
      console.error('[third-party-cart-checkout]', error);
      this.refs.loadingMessage.hidden = true;
      const message = missingSku
        ? this.dataset.errorMessageMissingSku || this.dataset.errorMessage || ''
        : this.dataset.errorMessage || '';
      this.#setError(message);
    } finally {
      this.#busy = false;
      this.refs.doneButton.disabled = false;
    }
  }

  /** @returns {Promise<void>} */
  handleDone = async () => {
    if (!this.#handoffUrl) {
      this.closeDialog();
      return;
    }
    if (!(await this.#clearCart())) return;
    await this.closeDialog();
    // The handoff is finished; an empty drawer left open under the QR modal is a dead end.
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer instanceof DialogComponent) await drawer.closeDialog();
  };

  /**
   * @param {MouseEvent} event
   * @returns {Promise<void>}
   */
  handleContinue = async (event) => {
    event.preventDefault();
    const url = this.#handoffUrl;
    if (!url) return;
    // Clear before leaving, as the TESO redirect does: the tab is gone afterwards.
    if (!(await this.#clearCart())) return;
    window.location.href = url;
  };

  /**
   * @returns {Promise<boolean>} Whether the cart was cleared.
   */
  async #clearCart() {
    if (this.#busy) return false;
    this.#busy = true;
    this.#setBusy(true);
    this.#setError('');

    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Failed to clear cart: ${response.status}`);
      const cart = await response.json();

      this.#handoffUrl = '';
      this.dispatchEvent(
        new CartUpdateEvent(cart, this.id, { itemCount: 0, source: 'third-party-cart-checkout' })
      );
      return true;
    } catch (error) {
      console.error('[third-party-cart-checkout]', error);
      this.#setError(this.dataset.errorMessageClear || this.dataset.errorMessage || '');
      return false;
    } finally {
      this.#busy = false;
      this.#setBusy(false);
    }
  }

  #reset() {
    const { qrHost, loadingMessage, deviceLink, doneButton } = this.refs;
    qrHost.replaceChildren();
    loadingMessage.hidden = false;
    deviceLink.hidden = true;
    deviceLink.removeAttribute('href');
    doneButton.disabled = true;
    this.#setError('');
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    const { doneButton } = this.refs;
    doneButton.disabled = busy;
    doneButton.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /**
   * @param {string} message
   */
  #setError(message) {
    const { errorMessage } = this.refs;
    errorMessage.textContent = message;
    errorMessage.hidden = !message;
  }
}

if (!customElements.get('third-party-cart-checkout-dialog')) {
  customElements.define('third-party-cart-checkout-dialog', ThirdPartyCartCheckoutDialog);
}

// Delegated: the cart drawer and cart page morph on every update, replacing the CTA node.
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const trigger = target?.closest('[data-third-party-cart-checkout-open]');
  if (!trigger || (trigger instanceof HTMLButtonElement && trigger.disabled)) return;

  event.preventDefault();
  const dialog = document.getElementById('third-party-cart-checkout-dialog');
  if (dialog instanceof ThirdPartyCartCheckoutDialog) {
    dialog.open();
  }
});
